"use client";

import { retriveBooking } from "api/booking/booking";
import {
  getCompanyInfoFun,
  getWebInfo,
  getWebsiteSectionInfo,
} from "api/company/company";
import { applyCoupon, removeCoupon } from "api/coupon/coupon";
import { getLocationData } from "api/locations/locations";
import { CreateStripeIntent } from "api/stripe/stripe";
import { VehiclePayload, VehicleResponse } from "api/types";
import { getAvailableVehicleData, getVehicleData } from "api/vehicles/vehicle";
import { LocationListingModal } from "models/LocationModal";
import { User } from "models/userModal";
import { CarListingModal } from "models/vehicleModal";
import moment from "moment";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import {
  getStoredRenterUser,
  purgeLegacyPersistedSession,
} from "utils/renterSession";
import { applyMarketingTags } from "analytics/marketingTags";
import { pickReturnTime, snapToOpenSlot } from "designs/Common/(car-search-form)/utils/returnTime";
import { onColor } from "utils/onColor";
import { primaryScale } from "utils/primaryScale";
import { trackEvent } from "analytics/posthog";
import { searchDatesCategory } from "analytics/metaPixel";
import {
  renterError,
  isBookingFor,
  isInvoiceFor,
  withCouponCleared,
  withInvoiceFrom,
  withRefetchedBooking,
  withServerInvoice,
  FLEET_MAX_ROWS,
  FleetPageBuffer,
  planFleetWave,
  waveEnd,
  formatDateTime,
} from "@1-now/booking-core";

interface BonzahProduct {
  name: string;
  productType: string;
  currency: string;
  regularPrice: number;
  discountPercent: number;
  finalPrice: number;
  totalPrice: number;
  numberOfDays: number;
  priceBasis: string;
  supplier: string;
  isSelected?: boolean;
  productInfo: {
    descriptionOfCoverageUrl: string;
  };
  additionalInfo?: Record<string, any>;
}
interface DataContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  ourVehicles: CarListingModal[];
  setOurVehicles: React.Dispatch<React.SetStateAction<CarListingModal[]>>;
  ourVehiclesLoading: boolean;
  /**
   * Whether the fleet request has finished, whatever the outcome. `ourVehicles`
   * is `[]` both before the call starts and after it fails, so on its own an
   * empty list cannot tell "not yet" from "nothing there" — and the car detail
   * pages read it as "not yet" forever (1NOW-1664).
   */
  ourVehiclesSettled: boolean;
  /** Renter-facing copy for a failed fleet load, or null when it succeeded. */
  ourVehiclesError: string | null;
  /** Re-runs the fleet request, so a failure can offer a retry rather than a dead end. */
  retryOurVehicles: () => void;
  ourLocation: LocationListingModal[];
  /**
   * Whether the branch request has finished, whatever the outcome. Same
   * reason `ourVehiclesSettled` exists: `ourLocation` is `[]` before the call
   * and after a failure, so a page restoring a branch from its URL cannot tell
   * "not yet" from "gone" without it.
   */
  ourLocationSettled: boolean;
  setOurLocation: React.Dispatch<React.SetStateAction<LocationListingModal[]>>;
  ourCompany: any;
  /**
   * True once the company-configuration fetch has SETTLED — success or
   * failure. `ourCompany` starts as `[]` and stays `[]` on failure, so its
   * emptiness cannot tell "still loading" from "there is no payment method",
   * and the checkout needs that distinction to decide between an optimistic
   * payment form and the "unavailable" notice.
   */
  ourCompanyResolved: boolean;
  /**
   * Renter-facing copy when the company-configuration request failed, or null.
   *
   * The configuration names the template this tenant renders, so the shell
   * cannot choose a router without it; App stops with a retry, the same way it
   * does for a failed website-details request.
   */
  ourCompanyError: string | null;
  setOurCompany: React.Dispatch<React.SetStateAction<any>>;
  companyDetails: any;
  setCompanyDetails: React.Dispatch<React.SetStateAction<any>>;
  banner: any;
  aboutUs: any;
  whyUs: any;
  imageSection: any;
  // Full footer Section row (heading, description, layout_variant, …). The
  // keypoints are already split into socialItems / address / contactItems /
  // footerItems below — `footer` exposes the parent Section's own metadata
  // (notably `layout_variant`) for the renderer to branch on.
  footer: any;
  socialItems: any;
  address: any;
  contactItems: any;
  footerItems: any;
  testimonials: any;
  faqs: any;
  popUp: any;
  setPopUp: React.Dispatch<React.SetStateAction<any>>;
  pickupLocation: any;
  setPickupLocation: React.Dispatch<
    React.SetStateAction<LocationListingModal | null>
  >;
  stripeVerificationLoading: boolean;
  setStripeVerificationLoading: React.Dispatch<React.SetStateAction<boolean>>;
  dropOffLocation: any;
  setDropoffLocation: React.Dispatch<
    React.SetStateAction<LocationListingModal | null>
  >;
  dropoffLocationType: boolean;
  setDropoffLocationType: React.Dispatch<React.SetStateAction<boolean>>;
  sameDropoffLocation: boolean;
  setSameDropoffLocation: React.Dispatch<React.SetStateAction<boolean>>;
  bookingdetails: any;
  setBookingdetails: React.Dispatch<React.SetStateAction<any>>;
  invoice: any;
  setInvoice: React.Dispatch<React.SetStateAction<any>>;
  // 1NOW-1095: per-cycle projection for multi-week / multi-month LTRs,
  // displayed before booking confirmation so the customer sees each
  // upcoming week's projected total (not just Week 1).
  weeklyQuote: any[];
  setWeeklyQuote: React.Dispatch<React.SetStateAction<any[]>>;
  acknowledgedExpensiveCycles: boolean;
  setAcknowledgedExpensiveCycles: React.Dispatch<React.SetStateAction<boolean>>;
  availableFleet: VehicleResponse[] | null;
  loadingAvailableFleet: boolean;
  /**
   * Renter-facing copy for the last availability search that failed, or null.
   * `availableFleet` is left as it was when the request fails, so without this
   * a failed search and one that found no cars looked the same to a results
   * page. Cleared as each search starts.
   */
  availableFleetError: string | null;
  isAuthPanelOpen: boolean;
  extras: any;
  setExtras: React.Dispatch<React.SetStateAction<[]>>;
  selectedExtras: any;
  setSelectedExtras: React.Dispatch<React.SetStateAction<Set<number>>>;
  selectedBonzahProducts: any;
  setSelectedBonzahProducts: React.Dispatch<React.SetStateAction<Set<any>>>;
  selectedBonzahAddOns: any;
  setSelectedBonzahAddOns: React.Dispatch<React.SetStateAction<Set<any>>>;
  bonzahProducts: any;
  setBonzahProducts: React.Dispatch<React.SetStateAction<BonzahProduct[]>>;
  setIsAuthPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  searchedPickupLoction: string;
  checkCouponResponseLoading: boolean;
  setCheckCouponResponseLoading: React.Dispatch<React.SetStateAction<boolean>>;
  loadingCompanyDetails: boolean;
  /**
   * Renter-facing copy when the website details request failed. The shell
   * cannot render without them, so App stops with a retry (1NOW-1664).
   */
  companyDetailsError: string | null;
  unavailable: boolean;
  allBookings: any;
  setAllBookings: React.Dispatch<React.SetStateAction<any>>;
  setUnavailable: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchedPickupLocation: React.Dispatch<React.SetStateAction<string>>;
  couponResponse: any;
  searchedDropoffLocation: string;
  setSearchedDropoffLocation: React.Dispatch<React.SetStateAction<string>>;
  setCouponResponse: React.Dispatch<React.SetStateAction<any>>;
  callCouponAPI: (couponCode: string, bookingId: string) => Promise<void>;
  callremoveCouponAPI: (couponCode: string, bookingId: string) => Promise<void>;
  selectedCar: CarListingModal | null;
  setSelectedCar: React.Dispatch<React.SetStateAction<CarListingModal | null>>;
  callAvailableFleetAPI: (navigate: any) => Promise<void>;
  checkout: (bookingId: any) => Promise<any>;

  startDate: Date | null;
  startHours: string;
  startMinutes: string;
  startPeriod: string;
  endDate: Date | null;
  endHours: string;
  endMinutes: string;
  endPeriod: string;
  setStartTime: React.Dispatch<React.SetStateAction<string>>;
  setEndTime: React.Dispatch<React.SetStateAction<string>>;
  /**
   * True once the renter (or a loaded booking) has fixed the return time. Until then the
   * return time follows the pick-up time (1NOW-11108). Reset when the search is cleared.
   */
  endTimeTouched: boolean;
  setEndTimeTouched: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * A screen that pre-fills the search from a saved booking holds this while it does, and calls the
   * function it returns when it is done (1NOW-11108). While any hold is up the pick-up time is
   * left exactly as the booking stored it; the pick-up snap is for new searches only.
   */
  holdSavedTrip: () => () => void;
  s_Time: string;
  e_Time: string;
  setIsLongTermRental: React.Dispatch<React.SetStateAction<boolean>>;
  is_long_term_rental: boolean;
  setLongTermRentalNature: React.Dispatch<React.SetStateAction<string>>;
  long_term_rental_nature: string;
  setLongTermRentalDuration: React.Dispatch<React.SetStateAction<number>>;
  long_term_rental_duration: number;

  // Hourly rental flag (1NOW-977). When true, the booking is priced by the
  // hour and uses the existing startDate/endDate/startTime/endTime fields
  // (which can now span across midnight with arbitrary durations).
  is_hourly_rental: boolean;
  setIsHourlyRental: React.Dispatch<React.SetStateAction<boolean>>;
  startTime: string;
  endTime: string;
  partiallyAvailableStartDate: Date | null;
  partiallyAvailableEndDate: Date | null;
  activeTab: string;
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
  selectedWeeks: string[];
  setSelectedWeeks: React.Dispatch<React.SetStateAction<string[]>>;
  weeksViewMonth: Date;
  setWeeksViewMonth: React.Dispatch<React.SetStateAction<Date>>;
  selectedMonth: number;
  setSelectedMonth: React.Dispatch<React.SetStateAction<number>>;
  monthStartDate: Date;
  setMonthStartDate: React.Dispatch<React.SetStateAction<Date>>;
  monthEndDate: Date;
  setMonthEndDate: React.Dispatch<React.SetStateAction<Date>>;

  driverFirstName: string;
  setDriverFirstName: React.Dispatch<React.SetStateAction<string>>;
  driverLastName: string;
  setDriverLastName: React.Dispatch<React.SetStateAction<string>>;
  driverEmail: string;
  setDriverEmail: React.Dispatch<React.SetStateAction<string>>;
  driverPhone: string;
  setDriverPhone: React.Dispatch<React.SetStateAction<string>>;

  firstName: string;
  setFirstName: React.Dispatch<React.SetStateAction<string>>;
  lastName: string;
  setLastName: React.Dispatch<React.SetStateAction<string>>;
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  phone: string;
  setPhone: React.Dispatch<React.SetStateAction<string>>;

  setStartDate: React.Dispatch<React.SetStateAction<Date | null>>;
  setStartHours: React.Dispatch<React.SetStateAction<string>>;
  setStartMinutes: React.Dispatch<React.SetStateAction<string>>;
  setStartPeriod: React.Dispatch<React.SetStateAction<string>>;
  setEndDate: React.Dispatch<React.SetStateAction<Date | null>>;
  setEndHours: React.Dispatch<React.SetStateAction<string>>;
  setEndMinutes: React.Dispatch<React.SetStateAction<string>>;
  setEndPeriod: React.Dispatch<React.SetStateAction<string>>;
  setPartiallyAvailableStartDate: React.Dispatch<
    React.SetStateAction<Date | null>
  >;
  setPartiallyAvailableEndDate: React.Dispatch<
    React.SetStateAction<Date | null>
  >;

  partiallyUnavailableTimes: { [key: string]: { from?: string; to?: string } };
  setPartiallyUnavailableTimes: React.Dispatch<
    React.SetStateAction<{ [key: string]: { from?: string; to?: string } }>
  >;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

// The payload date format is the package's formatDateTime (moved there
// unchanged): "2026-10-20T10:00:00", or "" for a missing date (ONENOW-493).

/** The pages that show one car, named by ?selectedCarId=. */
const CAR_PAGES = ["/listing-car-detail", "/car-listing-detail"];

/** `navigate`: the router's, when the provider sits inside one (index.tsx). */
export function DataProvider({ children, navigate: routerNavigate }: any) {
  const [user, setUser] = useState<User | null>(null);
  const [ourVehicles, setOurVehicles] = useState<CarListingModal[]>([]);
  const [ourLocation, setOurLocation] = useState<LocationListingModal[]>([]);
  const [ourLocationSettled, setOurLocationSettled] = useState<boolean>(false);
  const [ourCompany, setOurCompany] = useState<any>([]);
  const [ourCompanyResolved, setOurCompanyResolved] = useState(false);
  const [ourCompanyError, setOurCompanyError] = useState<string | null>(null);
  const [companyDetails, setCompanyDetails] = useState<any>();
  const [banner, setBanner] = useState<any>();
  const [aboutUs, setAboutUs] = useState<any>();
  const [whyUs, setWhyUs] = useState<any>();
  const [imageSection, setImageSection] = useState<any>();
  // Footer Section parent row — exposes layout_variant for the renderer.
  const [footer, setFooter] = useState<any>();
  const [testimonials, setTestimonials] = useState<any>();
  const [faqs, setFaqs] = useState<any>();
  const [popUp, setPopUp] = useState<any>();

  const [socialItems, setsocialItems] = useState<any>([]);
  const [address, setAddress] = useState<any>([]);
  const [contactItems, setContactItems] = useState<any>([]);
  const [footerItems, setFooterItems] = useState<any>([]);
  const [pickupLocation, setPickupLocation] =
    useState<LocationListingModal | null>(null);
  const [dropOffLocation, setDropoffLocation] =
    useState<LocationListingModal | null>(null);
  const [searchedPickupLoction, setSearchedPickupLocation] =
    useState<string>("");
  const [searchedDropoffLocation, setSearchedDropoffLocation] =
    useState<string>("");
  const [ourVehiclesLoading, setOurVehiclesLoading] = useState<boolean>(false);
  const [ourVehiclesSettled, setOurVehiclesSettled] = useState<boolean>(false);
  const [ourVehiclesError, setOurVehiclesError] = useState<string | null>(null);
  /**
   * Which fleet fetch is current. The pages of one run land over several
   * seconds and append as they arrive, so a retry started in the meantime
   * must be able to disown the older run's late pages — otherwise they would
   * be appended onto the new list and the fleet would show twice.
   */
  const vehiclesRun = useRef(0);
  const [dropoffLocationType, setDropoffLocationType] = useState<boolean>(true);
  const [sameDropoffLocation, setSameDropoffLocation] = useState<boolean>(true);
  const [isAuthPanelOpen, setIsAuthPanelOpen] = useState<boolean>(false);
  const [couponResponse, setCouponResponse] = useState<any>(null);
  const [checkCouponResponseLoading, setCheckCouponResponseLoading] =
    useState<boolean>(false);
  const [stripeVerificationLoading, setStripeVerificationLoading] =
    useState<boolean>(false);
  const [unavailable, setUnavailable] = useState<boolean>(false);
  const [selectedCar, setSelectedCar] = useState<CarListingModal | null>(null);
  const [bookingdetails, setBookingdetails] = useState<any>(null);
  /**
   * The booking as the renter last saw it, for the one callback that has to
   * put it back: a coupon removal the server refused. Written after each
   * commit rather than read from this render's closure, because the callers
   * hold whichever copy of the provider's functions they rendered with.
   */
  const committedBooking = useRef<any>(null);
  useEffect(() => {
    committedBooking.current = bookingdetails;
  }, [bookingdetails]);
  const [invoice, setInvoice] = useState<any>([]);
  const [extras, setExtras] = useState<any>([]);
  const [selectedExtras, setSelectedExtras] = useState<Set<number>>(new Set());
  const [allBookings, setAllBookings] = useState<any>();
  const [selectedBonzahProducts, setSelectedBonzahProducts] = useState<
    Set<any>
  >(new Set());
  const [selectedBonzahAddOns, setSelectedBonzahAddOns] = useState<Set<any>>(
    new Set(),
  );

  const [bonzahProducts, setBonzahProducts] = useState<BonzahProduct[]>([]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [driverFirstName, setDriverFirstName] = useState("");
  const [driverLastName, setDriverLastName] = useState("");
  const [driverEmail, setDriverEmail] = useState("");
  const [driverPhone, setDriverPhone] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [partiallyAvailableStartDate, setPartiallyAvailableStartDate] =
    useState<Date | null>(null);
  const [partiallyAvailableEndDate, setPartiallyAvailableEndDate] =
    useState<Date | null>(null);
  const [startHours, setStartHours] = useState("07");
  const [startMinutes, setStartMinutes] = useState("00");
  const [startPeriod, setStartPeriod] = useState("AM");
  const [activeTab, setActiveTab] = useState("dates");
  const [endHours, setEndHours] = useState("07");
  const [endMinutes, setEndMinutes] = useState("00");
  const [endPeriod, setEndPeriod] = useState("PM");
  const [selectedWeeks, setSelectedWeeks] = useState<string[]>([]);
  const [weeksViewMonth, setWeeksViewMonth] = useState<Date>(new Date());
  const [selectedMonth, setSelectedMonth] = useState<number>(0);
  const [monthStartDate, setMonthStartDate] = useState<Date>(new Date());
  const [monthEndDate, setMonthEndDate] = useState<Date>(
    moment().add(1, "months").toDate(),
  );

  const [availableFleet, setAvailableFleet] = useState<VehicleResponse[]>([]);
  const [loadingAvailableFleet, setLoadingAvailableFleet] =
    useState<boolean>(false);
  const [availableFleetError, setAvailableFleetError] = useState<
    string | null
  >(null);
  const [loadingCompanyDetails, setLoadingCompanyDetails] =
    useState<boolean>(true);
  const [companyDetailsError, setCompanyDetailsError] = useState<
    string | null
  >(null);
  const [partiallyUnavailableTimes, setPartiallyUnavailableTimes] = useState<{
    [key: string]: { from?: string; to?: string };
  }>({});
  const [is_long_term_rental, setIsLongTermRental] = useState<boolean>(true);
  const [long_term_rental_nature, setLongTermRentalNature] =
    useState<string>("daily");
  const [long_term_rental_duration, setLongTermRentalDuration] =
    useState<number>(0);
  const [weeklyQuote, setWeeklyQuote] = useState<any[]>([]);
  const [acknowledgedExpensiveCycles, setAcknowledgedExpensiveCycles] =
    useState<boolean>(false);

  // Hourly rental flag (1NOW-977)
  const [is_hourly_rental, setIsHourlyRental] = useState<boolean>(false);

  const s_Time = "07:00";
  const e_Time = "19:00";
  const [startTime, setStartTime] = useState(
    startDate ? moment(startDate).format("HH:mm") : s_Time,
  );
  // Designs A and B: the return time starts at the pick-up time and follows it until the renter
  // picks one, so an untouched 3-night trip is 3 days and not 4 (1NOW-11108). The effect below
  // does the following; this stays the app default, which is what Design C keeps.
  const [endTime, setEndTime] = useState(
    endDate ? moment(endDate).format("HH:mm") : e_Time,
  );
  const [endTimeTouched, setEndTimeTouched] = useState(false);

  useEffect(() => {
    if (!startDate && !endDate) setEndTimeTouched(false);
  }, [startDate, endDate]);

  // Screens that pre-fill the search from a saved booking (the Edit Booking page) hold this, and
  // the pick-up time they load is never snapped: a stored 10:15 stays 10:15.
  const savedTripHolds = useRef(0);
  const holdSavedTrip = useCallback(() => {
    savedTripHolds.current += 1;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      savedTripHolds.current -= 1;
    };
  }, []);

  // The pick-up time of a new search is always a slot the picker offers inside the pick-up
  // location's hours on the pick-up date (1NOW-11108): outside the window it goes to the day's
  // first open slot, off the half-hour grid inside it to the next one. It runs here, and not in
  // the time field, because the search, the car page and the bill all send `startTime` and the
  // field is not always mounted. A closed day gives null and is left to the closed-day handling.
  // Hourly rentals pick their own hours, and a trip loaded from
  // a saved booking is not a search (see holdSavedTrip).
  useEffect(() => {
    if (is_hourly_rental || !ourCompanyResolved || savedTripHolds.current > 0) return;
    const next = snapToOpenSlot(startTime, pickupLocation, startDate);
    if (next && next !== startTime) setStartTime(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startTime,
    startDate?.toDateString(),
    pickupLocation,
    is_hourly_rental,
    ourCompanyResolved,
    ourCompany?.default_template,
  ]);

  useEffect(() => {
    if (endTimeTouched || is_hourly_rental || !ourCompanyResolved) return;
    const dropLocation =
      dropoffLocationType === false && dropOffLocation
        ? dropOffLocation
        : pickupLocation;
    const next = pickReturnTime(startTime, dropLocation, endDate);
    if (next && next !== endTime) setEndTime(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startTime,
    endDate?.toDateString(),
    endTimeTouched,
    is_hourly_rental,
    pickupLocation,
    dropOffLocation,
    dropoffLocationType,
    ourCompanyResolved,
    ourCompany?.default_template,
  ]);

  useEffect(() => {
    apiCalls();
  }, []);

  // Keep favicon in sync when companyDetails data arrives (favicon_image is the source of truth)
  useEffect(() => {
    if (companyDetails?.favicon_image) {
      changeFavicon(companyDetails.favicon_image);
    }
  }, [companyDetails]);

  useEffect(() => {
    if (selectedCar) {
      if (
        !selectedCar.fleet_photos ||
        selectedCar.fleet_photos.length === 0 ||
        selectedCar.thumbnail_photo === null ||
        selectedCar.thumbnail_photo === selectedCar?.fleet_photos[0]?.id
      )
        return;
      const sortedPhotos = selectedCar.fleet_photos.sort((a, b) => {
        if (a.id === selectedCar.thumbnail_photo) return -1;
        if (b.id === selectedCar.thumbnail_photo) return 1;
        return 0;
      });

      setSelectedCar({ ...selectedCar, fleet_photos: sortedPhotos });
    }
  }, [selectedCar]);

  // Load the renter profile from the per-tab session (1NOW-1557).
  useEffect(() => {
    // Delete any credential left in localStorage by a build that persisted it.
    // A returning renter could otherwise carry a usable bearer token on their
    // device indefinitely — and nothing reads it any more, so keeping it is
    // pure risk. Runs before the read so a legacy value can't be adopted.
    purgeLegacyPersistedSession();
    // A blob that won't parse is cleared by the helper rather than left to fail
    // on every mount, which is the stuck state the old console-only catch left.
    const storedUser = getStoredRenterUser();
    if (storedUser) {
      setUser(storedUser);
    }
  }, []);

  /**
   * The whole fleet, and the slowest thing on the site by a wide margin.
   *
   * Measured against company 233 on dev: 188 vehicles, 1.31MB, and 5-7.5
   * seconds in one request — every other bootstrap call finishes in
   * 300-400ms — with the 30s read ceiling reached often enough that QA saw
   * "The request timed out" on the main listing (1NOW-10614). It runs on
   * mount, so the home page's fleet section holds a skeleton for the whole of
   * it.
   *
   * It is fetched in pages now. The endpoint honours limit/offset, and the
   * geometry — one small page for an early first paint, the rest wide and all
   * in flight together, since the per-request floor makes sequential pages
   * slower overall — is worked out and measured in utils/fleetPaging. Pages
   * are appended to `ourVehicles` as they arrive, in the backend's order, so
   * a consumer that reads the list sees a growing prefix of it: 24 cars at
   * ~2.7s, all 188 at about the time the single request used to land.
   * `ourVehiclesLoading` stays true until the last page, so the designs that
   * gate on it alone behave exactly as before; Design C reads the length as
   * well and paints early.
   *
   * Two things worth knowing before anyone tries to tune this from here.
   *
   * The date range below is inert. start_time and end_time are computed and
   * sent, and the response is byte-for-byte the same with a month range, a
   * single day, three days, or no dates at all — same 188 vehicles, same
   * ~1.31MB, same 20 seconds. Only the `available=true` search path in
   * getAvailableVehicleData actually filters. The dates are left in place
   * rather than deleted because the endpoint may yet learn to honour them.
   *
   * And the size is not really about vehicles. `extras` is 61% of the payload
   * — 797KB of it — because the full 17-entry catalogue is serialised onto
   * every one of the 188 rows, and across 40 sampled vehicles there were only
   * two distinct sets. `branches` repeats the same way at 1%. The frontend
   * only ever reads extras for the one selected car, never for a list, but it
   * cannot ask for just that one: /api/fleet/<id>/ answers 401 to a renter and
   * ?fleet=<id> is ignored, so a single vehicle costs the whole fleet. That
   * is a backend serializer problem, not something to work around here.
   */
  const callOurVehicleAPI = async () => {
    const run = ++vehiclesRun.current;
    const live = () => run === vehiclesRun.current;
    setOurVehiclesLoading(true);
    setOurVehiclesError(null);
    // A retry starts over: pages append, so the list has to be empty before
    // the first one lands, and "settled" has to be false again while it is
    // being rebuilt or the detail pages would call a car missing mid-fetch.
    setOurVehiclesSettled(false);
    setOurVehicles([]);
    try {
      const today = new Date();
      const firstDayOfMonth = `${today.getFullYear()}-${String(
        today.getMonth() + 1,
      ).padStart(2, "0")}-01`;
      const lastDay = new Date(
        today.getFullYear(),
        today.getMonth() + 1,
        0,
      ).getDate();
      const lastDayOfMonth = `${today.getFullYear()}-${String(
        today.getMonth() + 1,
      ).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      const payload: any = {
        start_time: firstDayOfMonth,
        end_time: lastDayOfMonth,
      };

      // Everything released so far, in order; `ourVehicles` is a copy of it
      // after each release. The buffer holds pages that land out of turn.
      const loaded: CarListingModal[] = [];
      const buffer = new FleetPageBuffer<CarListingModal>();

      let from = 0;
      while (!buffer.done && from < FLEET_MAX_ROWS) {
        const wave = planFleetWave(from);
        const failures = await Promise.all(
          wave.map(async (page) => {
            const data = await getVehicleData({ ...payload, ...page });
            if (!live()) return null;
            if (!data?.data || !Array.isArray(data.data)) {
              // getVehicleData swallows the failure and hands back `{ error }`,
              // so this is the ordinary network-failure path, not a malformed
              // payload. It used to raise "Invalid data format", which named
              // neither what happened nor what the renter could do about it.
              return data?.error || renterError(null, "vehicles");
            }
            const released = buffer.add(page, data.data as CarListingModal[]);
            if (released.length) {
              loaded.push(...released.filter((vehicle) => vehicle.active));
              setOurVehicles([...loaded]);
            }
            return null;
          }),
        );
        if (!live()) return;
        /* A page that failed past the end of the list is nothing lost — the
           end was released, so every page before it succeeded. A failure
           before the end leaves a hole the buffer will not release across,
           and what did arrive stays on screen with the error beside it. */
        const failure = failures.find((f): f is string => Boolean(f));
        if (failure && !buffer.done) throw new Error(failure);
        from = waveEnd(wave);
      }
    } catch (error: any) {
      if (!live()) return;
      const message = error?.message || renterError(error, "vehicles");
      setOurVehiclesError(message);
      toast.error(message);
      console.error("Vehicle API error:", error);
    } finally {
      if (live()) {
        setOurVehiclesLoading(false);
        setOurVehiclesSettled(true);
      }
    }
  };

  const callAvailableFleetAPI = async (navigate: (path: string) => void) => {
    setLoadingAvailableFleet(true);
    setAvailableFleetError(null);

    if (startDate === null) {
      toast.error("Start date is not set");
      setLoadingAvailableFleet(false);
      return;
    }

    if (endDate === null) {
      toast.error("End date is not set");
      setLoadingAvailableFleet(false);
      return;
    }

    if (!startTime) {
      toast.error("Start time is not set");
      setLoadingAvailableFleet(false);
      return;
    }

    if (!endTime) {
      toast.error("End time is not set");
      setLoadingAvailableFleet(false);
      return;
    }

    const vehiclePayload: VehiclePayload = {
      available: true,
      start_time: formatDateTime(startDate, startTime),
      end_time: formatDateTime(endDate, endTime),
      pickup_location: pickupLocation?.id,
      drop_off_location: dropOffLocation?.id,
      booking_id: bookingdetails?.id,
      is_long_term_rental : is_long_term_rental,
      long_term_rental_nature : long_term_rental_nature,
    };

    // 1NOW-11121: the renter's search passed the checks above (Meta `Search`):
    // the pick-up location, and the trip's dates as the content category.
    trackEvent("vehicle_search", {
      search_string: pickupLocation?.name,
      content_category: searchDatesCategory(vehiclePayload.start_time, vehiclePayload.end_time),
    });

    // Helper function to transform CarListingModal to VehicleResponse
    const transformCarListingToVehicleResponse = (
      carListing: CarListingModal[],
    ): VehicleResponse[] => {
      return carListing.map((car) => ({
        ...car,
      }));
    };

    try {
      const result = await getAvailableVehicleData(vehiclePayload);

      setLoadingAvailableFleet(false);

      if (result.error) {
        setLoadingAvailableFleet(false);
        setAvailableFleetError(result.error);
      } else if (result.data) {
        // Filter for active vehicles before transforming the result
        const activeVehicles = (result.data as CarListingModal[]).filter(
          (vehicle) => vehicle.active,
        );
        setAvailableFleet(transformCarListingToVehicleResponse(activeVehicles));
        navigate("/available-car");
      }
    } catch (error) {
      console.error("An unexpected error occurred:", error);
      setLoadingAvailableFleet(false);
      setAvailableFleetError(renterError(error, "vehicles"));
    } finally {
      setLoadingAvailableFleet(false);
    }
  };

  const callCouponAPI = async (couponCode: string, bookingId: string) => {
    try {
      setCheckCouponResponseLoading(true);
      const data: any = await applyCoupon(couponCode, bookingId);
      if (data?.error?.length > 0) {
        toast.error(data?.error);
      } else {
        setCouponResponse(data as any);

        if (data) {
          /* The server's re-priced invoice, all of it, straight away.
             apply_coupon answers with the booking's invoice as it now stands
             (see utils/couponInvoice). Only coupon_info and coupon_discount
             used to be copied across, with the totals left to the refetch
             below — so when that refetch failed the booking kept its
             pre-coupon total_price_after_coupon beside a coupon row, and the
             Pay button named a figure the server was no longer charging. */
          setBookingdetails((prev: any) =>
            withServerInvoice(prev, bookingId, data),
          );
          const response: any = await retriveBooking(bookingId);
          /* Only a real booking. retriveBooking answers with the booking on
             success and { error } on failure, and this used to write either
             one — so a refetch that failed replaced a perfectly good booking
             with an error object. Every screen after review reads
             `bookingdetails?.id`, so the renter was left holding a booking the
             server had created and the site could no longer see: no payment
             step, no verification step, and no way back to it. That is the
             blank checkout of 1NOW-9077, which ReviewPage guards against on
             the booking call and nothing guarded here. */
          setBookingdetails((prev: any) =>
            withRefetchedBooking(prev, bookingId, response),
          );
        }
      }
    } catch (error: any) {
      toast.error(renterError(error, "coupon"));
    } finally {
      setCheckCouponResponseLoading(false);
    }
  };
  /**
   * Take a coupon off, and never lose the booking doing it (bug-hunt #15).
   *
   * This wrote the booking refetch straight into `bookingdetails`, and
   * retriveBooking answers `{ error }` on failure — so a refetch that timed out
   * or dropped replaced the booking with an error object. Every later step
   * reads `bookingdetails.id`: the rail fell back to the quote, verification
   * and payment showed their "once your booking is confirmed" placeholders,
   * and the renter was left with a booking on the server and no way to pay
   * for it. The same failure the apply path above already guards against.
   *
   * A removal that the server itself refused was also silent: the row vanished
   * on the click and came back with the refetch, with nothing to say why.
   *
   * Now: the server's answer to the removal is the new invoice; the refetch
   * only refreshes the rest of the booking when it succeeds; a refused removal
   * says so, and if nothing fresher can be fetched the invoice the renter was
   * looking at is put back rather than left showing a removal that did not
   * happen.
   */
  const callremoveCouponAPI = async (couponCode: string, bookingId: string) => {
    const shown = committedBooking.current;
    try {
      // Optimistically clear the coupon from the invoice so the inline row
      // disappears immediately, then reconcile with the server's answer.
      setBookingdetails((prev: any) => withCouponCleared(prev, bookingId));
      const data: any = await removeCoupon(couponCode, bookingId);
      const removed = isInvoiceFor(data, bookingId);
      if (removed) {
        setBookingdetails((prev: any) => withServerInvoice(prev, bookingId, data));
      } else {
        toast.error("We couldn't remove your promo code. Please try again.");
      }

      const response: any = await retriveBooking(bookingId);
      if (isBookingFor(response, bookingId)) {
        setBookingdetails((prev: any) =>
          withRefetchedBooking(prev, bookingId, response),
        );
      } else if (!removed) {
        setBookingdetails((prev: any) => withInvoiceFrom(prev, shown));
      }
    } catch (error: any) {
      toast.error("We couldn't remove your promo code. Please try again.");
    }
  };

  const checkout = async (
    bookingId: any,
  ): Promise<{ data?: any; error?: string } | undefined> => {
    const payload = { booking_id: bookingId };

    try {
      const res = await CreateStripeIntent(payload);
      return res; // Return the result which can have data or error
    } catch (error: any) {
      toast.error(error.message || "Failed to process checkout");
      console.error("Checkout error:", error);
      return undefined; // Return undefined in case of an error
    }
  };

  // Fetch location data from API
  const callOurLocationAPI = async () => {
    try {
      const data = await getLocationData();
      if (Array.isArray(data)) {
        const locations = data as LocationListingModal[];
        setOurLocation(locations);
        const activeLocations = locations.filter((loc) => loc.active !== false);
        if (activeLocations.length === 1) {
          setPickupLocation(activeLocations[0]);
          setDropoffLocation(activeLocations[0]);
        }
      } else {
        throw new Error("Unable to fetch location data");
      }
    } catch (error: any) {
      toast.error(renterError(error, "locations"));
      console.error("Location API error:", error);
    } finally {
      setOurLocationSettled(true);
    }
  };

  const apiCalls = async () => {
    callOurCompanyDetails();
    callOurVehicleAPI();
    callOurLocationAPI();
    callOurCompanyAPI();
    callWebsiteSectionsAPI();
  };

  const changeFavicon = (url: any) => {
    if (!url) return;

    // Standard icon — used by Google Search to show favicon in search results
    const setLink = (rel: string, type?: string) => {
      let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement("link");
        link.rel = rel;
        if (type) link.type = type;
        document.head.appendChild(link);
      }
      link.href = url;
    };

    setLink("icon");
    setLink("shortcut icon");         // Older browsers / extra Googlebot signal
    setLink("apple-touch-icon");      // iOS home screen & some crawlers

    // <meta name="msapplication-TileImage"> for Windows / Bing
    let meta = document.querySelector<HTMLMetaElement>(
      'meta[name="msapplication-TileImage"]',
    );
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "msapplication-TileImage";
      document.head.appendChild(meta);
    }
    meta.content = url;
  };

  const callOurCompanyDetails = async () => {
    try {
      const data = await getWebInfo();
      // getWebInfo hands back `{ error }` on failure and nothing here checked
      // for it: the theme below was applied from undefined fields and the
      // renter got an unthemed page with no explanation. Surface it so the
      // shell can stop with a retry instead (1NOW-1664).
      if (data?.error) {
        setCompanyDetailsError(data.error);
        return;
      }
      setCompanyDetails(data);
      changeFavicon(data?.favicon_image);
      let fontName = data?.font_family ?? "Urbanist";
      fontName = fontName === "" ? "Urbanist" : fontName;
      const root = document.documentElement;
      const bannerHeadingColor = data?.banner_heading_color || "#ffffff";
      const bannerDescriptionColor =
        data?.banner_description_color || "#ffffff";
      const bgColor = data?.bg_color || "#000000";
      const primaryColor = data?.primary_color || "#000000";
      const secondaryColor = data?.component_bg_color || "#000000";
      const secondaryTextColor = data?.secondary_text_color || "#000000";
      const navigationTextColor = data?.navigation_text_color || "#000000";
      const textColor = data?.text_color || "#ffffff";
      const descriptionColor = data?.description_color || "#ffffff";
      const buttonColor = data?.button_color || "#3d5afe";
      const cardColor = data?.card_color || "#ffffff";
      const footerColor = data?.footer_color || "#F3F4F6";
      const footerTextColor = data?.footer_text_color || "#1F2937";

      const hexToRgb = (hex: string) => {
        const bigint = parseInt(hex.slice(1), 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return `${r}, ${g}, ${b}`;
      };

      // Auto-derived "on-primary" foreground. Picks whichever of near-white /
      // near-black has the higher WCAG contrast against the tenant's primary
      // color, so text + icons placed ON a primary-colored fill (chips,
      // badges, active pills, primary buttons) stay readable for ANY palette —
      // including dark primaries (e.g. burgundy) where the old `button_color`
      // (always a dark shade) was unreadable. Exposed as `--c-on-primary` and
      // recomputed on every styling update, so "Update Styling" can never land
      // on a low-contrast scheme.
      const _chan = (c: number) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
      };
      const _lum = (hex: string) => {
        const n = parseInt(hex.slice(1), 16);
        return (
          0.2126 * _chan((n >> 16) & 255) +
          0.7152 * _chan((n >> 8) & 255) +
          0.0722 * _chan(n & 255)
        );
      };
      const _contrast = (a: string, b: string) => {
        const la = _lum(a);
        const lb = _lum(b);
        return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
      };
      const onPrimaryColor =
        _contrast(primaryColor, "#FFFFFF") >=
        _contrast(primaryColor, "#111827")
          ? "#FFFFFF"
          : "#111827";
      const onPrimaryColorRgb = hexToRgb(onPrimaryColor);

      const bannerHeadingColorRgb = hexToRgb(bannerHeadingColor);
      const bannerDescriptionColorRgb = hexToRgb(bannerDescriptionColor);
      const bgColorRgb = hexToRgb(bgColor);
      const primaryColorRgb = hexToRgb(primaryColor);
      const secondaryColorRgb = hexToRgb(secondaryColor);
      const secondaryTextColorRgb = hexToRgb(secondaryTextColor);
      const navigationTextColorRgb = hexToRgb(navigationTextColor);
      const textColorRgb = hexToRgb(textColor);
      const descriptionColorRgb = hexToRgb(descriptionColor);
      const buttonColorRgb = hexToRgb(buttonColor);
      const cardColorRgb = hexToRgb(cardColor);
      const footerColorRgb = hexToRgb(footerColor);
      const footerTextColorRgb = hexToRgb(footerTextColor);
      root.style.setProperty("--c-banner-heading-color", bannerHeadingColorRgb);
      root.style.setProperty(
        "--c-banner-description-color",
        bannerDescriptionColorRgb,
      );
      // 1NOW-11136: a real scale (tints 50 to 400, the primary at 500 and 600,
      // shades 700 to 900), not ten copies of the primary.
      Object.entries(primaryScale(primaryColorRgb)).forEach(([step, rgb]) =>
        root.style.setProperty(`--c-primary-${step}`, rgb),
      );
      // The brand fill (`bg-primary-brand`): the primary itself, which the
      // tints above no longer are. Design C points it at the button colour.
      root.style.setProperty("--c-primary-brand", primaryColorRgb);
      root.style.setProperty("--c-on-primary", onPrimaryColorRgb);
      // 1NOW-11135: the label colour for anything filled with the button colour.
      root.style.setProperty("--c-on-button", hexToRgb(onColor(buttonColor)));
      root.style.setProperty("--c-secondary-50", secondaryColorRgb);
      root.style.setProperty("--c-secondary-100", secondaryColorRgb);
      root.style.setProperty("--c-secondary-200", secondaryColorRgb);
      root.style.setProperty("--c-secondary-300", secondaryColorRgb);
      root.style.setProperty("--c-secondary-400", secondaryColorRgb);
      root.style.setProperty("--c-secondary-500", secondaryColorRgb);
      root.style.setProperty("--c-secondary-600", secondaryColorRgb);
      root.style.setProperty("--c-secondary-700", secondaryColorRgb);
      root.style.setProperty("--c-secondary-800", secondaryColorRgb);
      root.style.setProperty("--c-secondary-900", secondaryColorRgb);
      root.style.setProperty("--c-bg-color", bgColorRgb);
      root.style.setProperty("--c-button-color", buttonColorRgb);
      root.style.setProperty("--c-text-color", textColorRgb);
      root.style.setProperty("--c-description-color", descriptionColorRgb);
      root.style.setProperty("--c-secondary-text-color", secondaryTextColorRgb);
      root.style.setProperty(
        "--c-navigation-text-color",
        navigationTextColorRgb,
      );
      root.style.setProperty("--c-card-color", cardColorRgb);
      root.style.setProperty("--c-footer-color", footerColorRgb);
      root.style.setProperty("--c-footer-text-color", footerTextColorRgb);
      document.documentElement.style.setProperty("--font-body", fontName);
      document.documentElement.style.setProperty("--font-display", fontName);
      // Optionally load the Google Font dynamically
      const formattedFont = fontName.replace(/\s+/g, "+");
      const fontUrl = `https://fonts.googleapis.com/css2?family=${formattedFont}:wght@100;200;300;400;500;600;700;800;900&display=swap`;

      let link = document.querySelector("#dynamic-font");
      if (!link) {
        link = document.createElement("link");
        link.id = "dynamic-font";
        (link as HTMLLinkElement).rel = "stylesheet";
        document.head.appendChild(link);
      }
      (link as HTMLLinkElement).href = fontUrl;
      // Marketing tags (analytics / ads / vendor snippets) — all config-driven
      // off CompanyWebsiteDetail, edited in FMS → Website Settings. Nothing
      // here knows about any specific company: the hardcoded per-company_id
      // chain this replaced, and the temporary fallback that carried its five
      // tenants until they were configured, are both gone (1NOW-1556).
      applyMarketingTags({
        googleTagId: data?.google_analytics_id,
        googleAdsId: data?.google_ads_id,
        customScripts: data?.custom_scripts,
        metaPixelId: data?.meta_pixel_id,
      });
    } catch (error: any) {
      toast.error(renterError(error, "company"));
      console.error("Company Details API error:", error);
    } finally {
      setLoadingCompanyDetails(false);
    }
  };
  const callOurCompanyAPI = async () => {
    try {
      const data = await getCompanyInfoFun();
      /* getCompanyInfoFun hands back `{ error }` on failure instead of
         throwing, so the catch below never saw one: the error object was
         stored as the configuration. That is the request App reads
         `default_template` from, so a design3 tenant whose configuration timed
         out or 500'd rendered Design A — for the life of the page, with no
         message — and every other reader of ourCompany (the identity default,
         long-term rentals, the operator's phone and email) quietly read
         undefined. The same guard getWebInfo got for 1NOW-1664. `ourCompany`
         stays `[]` on failure, as the ourCompanyResolved note promises. */
      if (!data || typeof data !== "object" || Array.isArray(data) || data.error) {
        setOurCompanyError(data?.error || renterError(null, "company"));
        return;
      }
      setOurCompany(data);
    } catch (error: any) {
      setOurCompanyError(renterError(error, "company"));
      console.error("Company API error:", error);
    } finally {
      // Settled either way. A failure MUST flip this too, or the checkout's
      // optimistic payment form would sit there forever on a host whose
      // config never loads, instead of falling back to the notice.
      setOurCompanyResolved(true);
    }
  };

  const callWebsiteSectionsAPI = async () => {
    try {
      const sections = await getWebsiteSectionInfo();
      // On failure this hands back `{ error }`, and an object is truthy — so
      // `?.forEach` threw, and the catch below put the raw TypeError text
      // ("sections.forEach is not a function") in front of the renter. Check
      // the shape we actually need instead of trusting truthiness.
      if (!Array.isArray(sections)) {
        throw new Error(sections?.error || renterError(null, "company"));
      }
      sections.forEach((section: any) => {
        switch (section.section_type) {
          case "about-us":
            setAboutUs(section);
            break;
          case "banner":
            setBanner(section);
            break;
          case "faqs":
            setFaqs(section);
            break;
          case "pop-up":
            setPopUp(section);
            break;
          case "testimonials":
            setTestimonials(section);
            break;
          case "why-us":
            setWhyUs(section);
            break;
          case "image-section":
            setImageSection(section);
            break;
          case "footer":
            let socialItemsList: any = [];
            let contactItemsList: any = [];
            let addressItemsList: any = [];
            let footerItemsList: any = [];
            section?.keypoints?.forEach((keypoint: any, index: number) => {
              if (keypoint?.keypoint_type === "social_items") {
                socialItemsList.push({
                  index: index,
                  id: keypoint.id.toString(),
                  heading: keypoint.heading,
                  description: keypoint.description,
                  image_url: keypoint.image_url,
                  keypoint_type: keypoint.keypoint_type,
                });
              } else if (keypoint?.keypoint_type === "contact_items") {
                contactItemsList.push({
                  index: index,
                  id: keypoint.id.toString(),
                  heading: keypoint.heading,
                  description: keypoint.description,
                  link: keypoint.link,
                  image_url: keypoint.image_url,
                  keypoint_type: keypoint.keypoint_type,
                });
              } else if (keypoint?.keypoint_type === "address_items") {
                addressItemsList.push({
                  index: index,
                  id: keypoint.id.toString(),
                  heading: keypoint.heading,
                  description: keypoint.description,
                  image_url: keypoint.image_url,
                  keypoint_type: keypoint.keypoint_type,
                });
              } else {
                footerItemsList.push({
                  index: index,
                  id: keypoint.id.toString(),
                  heading: keypoint.heading,
                  description: keypoint.description,
                  image_url: keypoint.image_url,
                  keypoint_type: keypoint.keypoint_type,
                });
              }
            });
            setsocialItems(socialItemsList);
            setContactItems(contactItemsList);
            setAddress(addressItemsList);
            setFooterItems(footerItemsList);
            // Store the parent Section too so the renderer can branch on
            // `footer.layout_variant`. We only need the metadata; keypoints
            // are already split above into per-type buckets.
            setFooter(section);
            break;
          default:
            break;
        }
      });
    } catch (error: any) {
      toast.error(renterError(error, "company"));
    }
  };

  /* The car the URL was last made to name, so a deselection can take back
     exactly that key and nothing else. */
  const namedCarId = useRef<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (selectedCar && selectedCar.id) {
      params.delete(String(selectedCar.id));
      params.set("selectedCarId", String(selectedCar.id));
    } else {
      Array.from(params.keys()).forEach((key) => {
        if (/^\d+$/.test(key)) params.delete(key);
      });
      /* A car that was selected and no longer is: the URL stops naming it.
         Design C's grid deselects when its panel closes, and a key left
         behind said a car was chosen when none was (1NOW-10785). Only the
         id this effect wrote — never a selectedCarId that arrived in the URL
         before any car was selected, which the effect below still has to
         read on a reload. */
      if (
        namedCarId.current !== null &&
        params.get("selectedCarId") === namedCarId.current
      ) {
        params.delete("selectedCarId");
      }
    }
    namedCarId.current =
      selectedCar && selectedCar.id ? String(selectedCar.id) : null;

    // The hash stays: "/#section-faqs" has no query, so this used to rewrite it to "/" on
    // every load and drop the section the URL named (1NOW-11111).
    const newUrl =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : "") +
      window.location.hash;
    if (window.location.search !== `?${params.toString()}`) {
      window.history.replaceState({}, "", newUrl);
    }
  }, [selectedCar]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const carIdParam = params.get("selectedCarId");

    if (carIdParam) {
      if (!selectedCar || String(selectedCar.id) !== carIdParam) {
        const car = ourVehicles.find((v) => String(v.id) === carIdParam);

        if (car) {
          setSelectedCar(car);
        } else if (ourVehicles.length > 0 && selectedCar) {
          setSelectedCar(null);
        }
      }
    } else if (selectedCar && CAR_PAGES.includes(window.location.pathname)) {
      /* On a car page the selected car is the page. An address that doesn't name it (a way in that
         didn't, or a step written from the router's copy of the address) gets the id back instead of
         the page losing its car: a car page with no car priced the trip with none, "Vehicle not
         found." (1NOW-11383, Faiza 9 Oct). Through the router when we have it, so what is built from
         the router's address (the booking steps' ?step=) keeps the id. */
      params.set("selectedCarId", String(selectedCar.id));
      const to = { pathname: window.location.pathname, search: `?${params}`, hash: window.location.hash };
      if (routerNavigate) routerNavigate(to, { replace: true });
      else window.history.replaceState(window.history.state, "", to.pathname + to.search + to.hash);
    } else if (selectedCar) {
      setSelectedCar(null);
    }
  }, [window.location.pathname, window.location.search, ourVehicles]);

  return (
    <DataContext.Provider
      value={{
        user,
        setUser,
        ourVehicles,
        setOurVehicles,
        ourVehiclesLoading,
        ourVehiclesSettled,
        ourVehiclesError,
        retryOurVehicles: callOurVehicleAPI,
        isAuthPanelOpen,
        setIsAuthPanelOpen,
        ourLocation,
        ourLocationSettled,
        loadingCompanyDetails,
        companyDetailsError,
        checkCouponResponseLoading,
        setCheckCouponResponseLoading,
        couponResponse,
        selectedExtras,
        setSelectedExtras,
        selectedBonzahProducts,
        setSelectedBonzahProducts,
        selectedBonzahAddOns,
        setSelectedBonzahAddOns,
        setOurLocation,
        bookingdetails,
        setBookingdetails,
        ourCompany,
        ourCompanyResolved,
        ourCompanyError,
        setOurCompany,
        companyDetails,
        setCompanyDetails,
        banner,
        aboutUs,
        whyUs,
        imageSection,
        footer,
        socialItems,
        address,
        contactItems,
        footerItems,
        testimonials,
        faqs,
        setPopUp,
        popUp,
        extras,
        setExtras,
        setBonzahProducts,
        bonzahProducts,
        setCouponResponse,
        availableFleet,
        availableFleetError,
        allBookings,
        setAllBookings,
        pickupLocation,
        unavailable,
        setUnavailable,
        sameDropoffLocation,
        setSameDropoffLocation,
        setPickupLocation,
        dropOffLocation,
        setDropoffLocation,
        searchedDropoffLocation,
        setStripeVerificationLoading,
        stripeVerificationLoading,
        searchedPickupLoction,
        setSearchedDropoffLocation,
        firstName,
        lastName,
        email,
        phone,
        setFirstName,
        setLastName,
        setEmail,
        setPhone,
        driverEmail,
        setDriverEmail,
        driverFirstName,
        driverLastName,
        driverPhone,
        setDriverFirstName,
        setDriverLastName,
        setDriverPhone,
        setSearchedPickupLocation,
        dropoffLocationType,
        setDropoffLocationType,
        invoice,
        setInvoice,
        weeklyQuote,
        setWeeklyQuote,
        acknowledgedExpensiveCycles,
        setAcknowledgedExpensiveCycles,
        loadingAvailableFleet,
        callCouponAPI,
        callremoveCouponAPI,
        selectedCar,
        setSelectedCar,
        callAvailableFleetAPI,
        startDate,
        startMinutes,
        startHours,
        startPeriod,
        activeTab,
        setActiveTab,
        endDate,
        endMinutes,
        endPeriod,
        partiallyAvailableStartDate,
        partiallyAvailableEndDate,
        endHours,
        setStartDate,
        setStartHours,
        checkout,
        setStartMinutes,
        setStartPeriod,
        setEndDate,
        setEndHours,
        setEndMinutes,
        setEndPeriod,
        setEndTime,
        endTimeTouched,
        setEndTimeTouched,
        holdSavedTrip,
        setStartTime,
        startTime,
        endTime,
        s_Time,
        e_Time,
        setIsLongTermRental,
        is_long_term_rental,
        setLongTermRentalNature,
        long_term_rental_nature,
        setLongTermRentalDuration,
        long_term_rental_duration,
        is_hourly_rental,
        setIsHourlyRental,
        setPartiallyAvailableStartDate,
        setPartiallyAvailableEndDate,
        selectedWeeks,
        setSelectedWeeks,
        weeksViewMonth,
        setWeeksViewMonth,
        selectedMonth,
        setSelectedMonth,
        monthStartDate,
        setMonthStartDate,
        monthEndDate,
        setMonthEndDate,
        partiallyUnavailableTimes,
        setPartiallyUnavailableTimes,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
}
